import { FC } from 'react'
import {
  FileText,
  Upload,
  Download,
  File as FileIcon,
  Image as ImageIcon,
  Film,
  Share2,
  Trash2,
  ArrowRight,
  Database,
  Shield,
  File
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Header from '@/components/Header'
import { Motion } from './ui/motion'
import { Typography } from './ui/typography'
import { Button } from './ui/button'

// Static map: Tailwind cannot see classes built by string interpolation,
// so accent variants have to be spelled out in full.
const accentClasses = {
  primary: 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]',
  secondary: 'bg-[var(--brand-dark)]/10 text-[var(--brand-dark)]',
  accent: 'bg-[var(--brand-blue)]/40 text-[var(--brand-dark)]',
  neutral: 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]'
} as const

type AccentColor = keyof typeof accentClasses

const cardClass = 'rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]'

const MockDashboard: FC = () => {
  const navigate = useNavigate()
  const uuid = 'timestorage-demo'

  // Mock storage data
  const storageStats = {
    totalSpace: '100 GB',
    usedSpace: '42 GB',
    availableSpace: '58 GB',
    percentUsed: 42,
    usedPercentage: 42
  }

  // Mock file categories
  const fileCategories = [
    {
      id: 'documents',
      title: 'Documents',
      count: 156,
      icon: <FileText className='h-6 w-6' />,
      size: '12.4 GB',
      color: 'primary' as AccentColor
    },
    {
      id: 'images',
      title: 'Images',
      count: 342,
      icon: <ImageIcon className='h-6 w-6' />,
      size: '18.7 GB',
      color: 'secondary' as AccentColor
    },
    {
      id: 'videos',
      title: 'Videos',
      count: 28,
      icon: <Film className='h-6 w-6' />,
      size: '9.2 GB',
      color: 'accent' as AccentColor
    },
    {
      id: 'other',
      title: 'Other Files',
      count: 64,
      icon: <File className='h-6 w-6' />,
      size: '1.7 GB',
      color: 'neutral' as AccentColor
    }
  ]

  // Mock recent activities
  const recentActivities = [
    {
      id: 1,
      action: 'Upload',
      icon: <Upload className='h-4 w-4' />,
      filename: 'project_report.pdf',
      timestamp: '2 hours ago',
      color: 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]'
    },
    {
      id: 2,
      action: 'Download',
      icon: <Download className='h-4 w-4' />,
      filename: 'vacation_photos.zip',
      timestamp: '5 hours ago',
      color: 'bg-[var(--brand-blue)]/40 text-[var(--brand-dark)]'
    },
    {
      id: 3,
      action: 'Share',
      icon: <Share2 className='h-4 w-4' />,
      filename: 'presentation.pptx',
      timestamp: 'Yesterday',
      color: 'bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]'
    },
    {
      id: 4,
      action: 'Delete',
      icon: <Trash2 className='h-4 w-4' />,
      filename: 'old_backups.zip',
      timestamp: '3 days ago',
      color: 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]'
    }
  ]

  // Note: Security status can be implemented as needed in the future
  // const securityStatus = {
  //   isSecure: true,
  //   lastScan: '2 hours ago',
  //   threatsDetected: 0,
  //   icon: <Shield className="h-6 w-6 text-[hsl(var(--success))]" />
  // }

  // Icon helper function - kept for potential future use
  // const getActionIcon = (action: string) => {
  //   switch (action.toLowerCase()) {
  //     case 'upload':
  //       return <Upload className="h-4 w-4" />
  //     case 'download':
  //       return <Download className="h-4 w-4" />
  //     case 'share':
  //       return <Share2 className="h-4 w-4" />
  //     case 'delete':
  //       return <Trash2 className="h-4 w-4" />
  //     default:
  //       return <File className="h-4 w-4" />
  //   }
  // }

  return (
    <div className='min-h-screen bg-[hsl(var(--background))]'>
      <Header title={`TimeStorage - ${uuid}`} />

      <div className='container mx-auto px-4 py-6'>
        {/* Welcome Section */}
        <Motion variant='fadeIn'>
          <div className='mb-8'>
            <h1 className='text-2xl font-semibold tracking-tight mb-1'>Welcome back!</h1>
            <p className='text-sm text-[hsl(var(--muted-foreground))]'>Here's what's happening with your storage</p>
          </div>
        </Motion>

        {/* Stats Grid */}
        <Motion variant='slideUp' delay={0.1}>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8'>
            {[
              {
                label: 'Total Storage',
                value: `${storageStats.usedSpace} / ${storageStats.totalSpace}`,
                icon: <Database className='h-6 w-6' />,
                color: 'primary' as AccentColor
              },
              {
                label: 'Files',
                value: '520',
                icon: <FileIcon className='h-6 w-6' />,
                color: 'secondary' as AccentColor
              },
              {
                label: 'Shared',
                value: '10',
                icon: <Share2 className='h-6 w-6' />,
                color: 'accent' as AccentColor
              },
              {
                label: 'Trash',
                value: '5',
                icon: <Trash2 className='h-6 w-6' />,
                color: 'neutral' as AccentColor
              }
            ].map((stat, index) => (
              <div key={index} className={`${cardClass} card-hover`}>
                <div className='p-4'>
                  <div className='flex items-center justify-between gap-3'>
                    <div>
                      <p className='text-sm text-[hsl(var(--muted-foreground))]'>{stat.label}</p>
                      <h3 className='text-2xl font-semibold'>{stat.value}</h3>
                    </div>
                    <div className={`p-3 rounded-full ${accentClasses[stat.color]}`}>{stat.icon}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Motion>

        {/* File Categories Section */}
        <Motion variant='slideUp' delay={0.2}>
          <section className='mb-8'>
            <h2 className='text-xl font-semibold mb-4'>File Categories</h2>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
              {fileCategories.map((category, index) => (
                <div
                  key={index}
                  className={`${cardClass} card-hover cursor-pointer`}
                  onClick={() => navigate(`/files?type=${category.id}`)}
                >
                  <div className='p-4'>
                    <div className='flex items-center gap-4'>
                      <div className={`p-3 rounded-lg ${accentClasses[category.color]}`}>{category.icon}</div>
                      <div>
                        <h3 className='font-semibold'>{category.title}</h3>
                        <Typography variant='body2' color='neutral'>
                          {category.count} files • {category.size}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </Motion>

        {/* Recent Activity */}
        <Motion variant='slideUp' delay={0.3}>
          <section className='mb-8'>
            <h2 className='text-xl font-semibold mb-4'>Recent Activity</h2>
            <div className={`${cardClass} overflow-hidden`}>
              <ul className='divide-y divide-[hsl(var(--border))]'>
                {recentActivities.map(activity => (
                  <li
                    key={activity.id}
                    className='flex items-center justify-between gap-3 p-4 cursor-pointer transition-colors hover:bg-[hsl(var(--muted))]'
                  >
                    <div className='flex items-center gap-3'>
                      <div className={`p-2 rounded-full ${activity.color}`}>{activity.icon}</div>
                      <div>
                        <div className='font-medium'>{activity.filename}</div>
                        <div className='text-sm text-[hsl(var(--muted-foreground))]'>
                          {activity.action} • {activity.timestamp}
                        </div>
                      </div>
                    </div>
                    <Button variant='ghost' size='icon' aria-label={`Open ${activity.filename}`}>
                      <ArrowRight className='h-4 w-4' />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </Motion>

        {/* Security Status */}
        <Motion variant='slideUp' delay={0.4}>
          <section className='mb-8'>
            <h2 className='text-xl font-semibold mb-4'>Security Status</h2>
            <div className={cardClass}>
              <div className='p-5'>
                <div className='flex items-center justify-between gap-3 mb-4'>
                  <div>
                    <h3 className='font-semibold'>Account Protection</h3>
                    <Typography variant='body2' color='neutral'>
                      Your account is secured with Auth0
                    </Typography>
                  </div>
                  <div className='p-2 rounded-full bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]'>
                    <Shield className='h-6 w-6' />
                  </div>
                </div>

                <div className='flex items-center justify-between gap-3'>
                  <div>
                    <h3 className='font-semibold'>File Encryption</h3>
                    <Typography variant='body2' color='neutral'>
                      All files are encrypted at rest
                    </Typography>
                  </div>
                  <div className='p-2 rounded-full bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]'>
                    <Shield className='h-6 w-6' />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </Motion>
      </div>
    </div>
  )
}

export default MockDashboard
